import { currentUserVersion, LOG_API_URL } from '@/globals/env'
import { useAuthStore } from '@/stores/auth'
import type { ClientLog } from '@/types/ClientLog'

// New batching constants
const BATCH_SIZE_LIMIT = 100 // Send logs after collecting this many entries
const BATCH_TIME_LIMIT_MS = 30000 // Send logs after this duration (30 seconds)
const PAYLOAD_SIZE_LIMIT_BYTES = 250000 // Max payload size before sending (250KB)

// Global log queue and timer
const logQueue: ClientLog[] = []
let batchTimer: number | null = null

// ----------------------------------------------------------------------
// sendLogToBackend Function
// ----------------------------------------------------------------------

/**
 * Processes the log queue and sends the batch payload to the backend.
 */
async function dispatchLogBatch() {
  if (logQueue.length === 0) {
    return
  }

  // 1. Clear the timer and queue *before* the asynchronous request
  if (batchTimer !== null) {
    clearTimeout(batchTimer)
    batchTimer = null
  }
  const batchToSend = logQueue.splice(0, logQueue.length) // Take all logs from the queue

  // 2. Prepare the final payload
  const authStore = useAuthStore()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }

  if (authStore?.user?.token) {
    headers['Authorization'] = `Bearer ${authStore.user.token}`
  }

  // Wrap the array of log objects into a single payload
  const finalPayload = {
    logs: batchToSend
  }

  // 3. Send the POST request
  try {
    await fetch(LOG_API_URL, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(finalPayload)
    })
  } catch (e) {
    console.error('Failed to send log batch to backend:', e)
  }
}
/**
 * Queues a single log entry and checks batch limits for dispatch.
 */
function queueLogEntry(logDetails: ClientLog) {
  if (logDetails.level == 'info') return

  const finalLogEntry: ClientLog = {
    ...logDetails
  }

  logQueue.push(finalLogEntry)

  // Start or reset the time limit timer
  if (batchTimer === null) {
    batchTimer = setTimeout(dispatchLogBatch, BATCH_TIME_LIMIT_MS)
  }

  // 6. Check size and count limits
  const currentPayloadSize = JSON.stringify({ logs: logQueue }).length

  if (logQueue.length >= BATCH_SIZE_LIMIT || currentPayloadSize >= PAYLOAD_SIZE_LIMIT_BYTES) {
    // Dispatch synchronously if a limit is reached
    dispatchLogBatch()
  }
}

// ----------------------------------------------------------------------
// WebsiteLogger Export
// ----------------------------------------------------------------------

export const WebsiteLogger = {
  // Website's own logging methods
  info: (message: string, context?: any) =>
    queueLogEntry({
      level: 'info',
      message,
      context,
      source: 'website',
      version: currentUserVersion
    }),
  error: (message: string, context?: any) =>
    queueLogEntry({
      level: 'error',
      message,
      context,
      source: 'website',
      version: currentUserVersion
    }),

  /**
   * Initializes the listener for extension messages (which come through the iframe).
   */
  initExtensionListener: () => {
    window.addEventListener(
      'message',
      (event: MessageEvent) => {
        // TODO: Security: Check if the message is from your expected origin

        // 2. Check for the extension log identifier
        if (event.data && event.data.type === 'EXTENSION_LOG' && event.data.log) {
          const extLog = event.data.log

          const logDetails: ClientLog = {
            level: extLog.level,
            version: extLog?.version ?? 'unknown',
            message: extLog.message,
            context: extLog.context,
            store: extLog.store,
            source: 'extension',
            country: extLog.country
          }

          // queueLogEntry handles all sanitization
          queueLogEntry(logDetails)
        }
      },
      false
    )
  }
}
